<script setup lang="ts">
const shardPng = '/img/2026/shard-faq.png';

interface AccordionItem {
    headline?: string;
    copy?: string;
    image?: { src?: string; altText?: string };
    anchorId?: string;
}

interface FaqButton {
    label?: string;
    title?: string;
    url?: string;
}

const props = withDefaults(defineProps<{
    headline?: string;
    subHead?: string;
    button?: FaqButton;
    teal?: boolean;
    accordionItems?: AccordionItem[];
}>(), {
    headline: 'Experience UNBOUND',
});

const openIndex = ref(-1);

const defaultItems = [
    {
        question: 'See whats next',
        answer: 'From AI shifts to marketing trends, change can feel overwhelming. Through our content, speakers, and experiences, INBOUND will break it down and show you what matters most so you leave with a clearer view of where the industry is going and how to move <span class="underline decoration-dotted underline-offset-[3px]">forward with confidence</span>.',
    },
    {
        question: 'Grow your network',
        answer: 'Connect with thousands of professionals, industry leaders, and peers who share your drive. Build meaningful relationships that last well beyond the event.',
    },
    {
        question: 'Increase your skills',
        answer: 'Dive into hands-on workshops, breakout sessions, and expert-led talks designed to sharpen your skills and give you practical takeaways you can use right away.',
    },
];

const items = computed(() => {
    if (props.accordionItems?.length) {
        return props.accordionItems.map(item => ({
            question: item.headline || '',
            answer: item.copy || (item as any).content || '',
        }));
    }
    return defaultItems;
});

const toggle = (index) => {
    openIndex.value = openIndex.value === index ? -1 : index;
};

const faqItems = useTemplateRef('faqItems');
const visible = ref(new Set());

onMounted(() => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                visible.value = new Set([...visible.value, entry.target.dataset.faqIndex]);
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0.15 });

    faqItems.value?.forEach((el) => observer.observe(el));
});

const stripHtml = (html) => html.replace(/<[^>]*>/g, '');

useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: items.value.map((item) => ({
                    '@type': 'Question',
                    name: item.question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: stripHtml(item.answer),
                    },
                })),
            }),
        },
    ],
});
</script>

<template>
    <section class="block-2026-faq relative isolate overflow-hidden
        flex flex-col gap-[31px] items-start
        px-[24px] pt-[60px] pb-[60px]
        md:pl-[385px] md:pr-[40px] md:pt-[90px] md:pb-[100px]">

        <!-- Background (decorative) -->
        <div aria-hidden="true" class="absolute inset-0 z-0 pointer-events-none overflow-visible">
            <!-- Amber glow (top right) -->
            <div class="absolute w-[350px] h-[350px] rounded-full bg-[#FF9700] blur-[100px]
                right-[5%] top-[-322px]
                md:left-[1020px] md:right-auto" />

            <!-- Shard PNG (desktop only) -->
            <div class="hidden md:flex absolute left-[-750px] top-[-404px] w-[1150px] h-[1141px] items-center justify-center">
                <img :src="shardPng" loading="lazy" decoding="async" alt="" class="w-[822px] h-[848px] -rotate-[59deg] flex-none object-cover" />
            </div>

            <!-- Orange glow (top left) -->
            <div class="absolute w-[346px] h-[346px] rounded-full bg-[var(--orange)] blur-[75px]
                left-[-58px] top-[-154px]" />
        </div>

        <!-- Heading + Accordion group -->
        <div class="relative z-[1] flex flex-col gap-[31px] md:gap-[69px] w-full">
            <h2 class="font-hubspot-serif text-white
                text-[34px] leading-[1.1]
                md:text-[calc(60/var(--base-fs)*1rem)]">
                {{ headline }}
            </h2>

            <!-- Accordion -->
            <div class="flex flex-col gap-[16px] md:gap-[30px] w-full">
                <div v-for="(item, index) in items" :key="index"
                    ref="faqItems" :data-faq-index="index"
                    class="faq-reveal" :class="{ 'is-visible': visible.has(String(index)) }"
                    :style="{ transitionDelay: `${index * 150}ms` }">
                    <div :class="openIndex === index ? 'pb-[30px] md:pb-[34px]' : 'pb-[18px] md:pb-[34px]'"
                        class="border-b border-white/50">
                        <button
                            :id="`faq-trigger-${index}`"
                            :aria-expanded="openIndex === index"
                            :aria-controls="`faq-panel-${index}`"
                            class="group flex items-center md:items-start justify-between w-full gap-[30px] md:gap-[77px]
                                text-left cursor-pointer bg-transparent border-none p-0
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white
                                focus-visible:ring-offset-4 focus-visible:ring-offset-transparent rounded-sm"
                            @click="toggle(index)">
                            <span class="font-hubspot-serif text-white
                                text-[26px] leading-[1.1]
                                md:text-[calc(36/var(--base-fs)*1rem)]">
                                {{ item.question }}
                            </span>
                            <span aria-hidden="true" class="flex-none md:mt-[3px]">
                                <svg viewBox="0 0 37 37" class="w-[37px] h-[37px]" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="18.5" cy="18.5" r="17.5" stroke-width="1"
                                        class="stroke-white fill-transparent transition-[fill] duration-200 group-hover:fill-white" />
                                    <line x1="12.75" y1="18.5" x2="24.25" y2="18.5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                                        class="stroke-white transition-[stroke] duration-200 group-hover:stroke-(--orange)" />
                                    <line x1="18.5" y1="12.75" x2="18.5" y2="24.25" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                                        :class="openIndex === index && 'opacity-0'"
                                        class="stroke-white transition-[opacity,stroke] duration-300 group-hover:stroke-(--orange)" />
                                </svg>
                            </span>
                        </button>
                        <div
                            :id="`faq-panel-${index}`"
                            role="region"
                            :aria-labelledby="`faq-trigger-${index}`"
                            class="faq-answer"
                            :class="{ 'is-open': openIndex === index }">
                            <div class="overflow-hidden">
                                <div class="font-hubspot-sans font-light text-white
                                    text-[16px] leading-[26px] tracking-[0.32px]
                                    md:text-[calc(16/var(--base-fs)*1rem)]
                                    pt-[12px] md:pt-[13px] max-w-[900px]"
                                    v-html="item.answer" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- CTA Button -->
        <MessLink v-if="button?.url" :href="button.url" :label="button.title"
            class="relative z-[1] font-hubspot-serif inline-flex items-center gap-2.5
            px-5 py-2.5
            bg-white text-(--magenta) no-underline
            hover:bg-(--magenta) hover:text-white transition-colors duration-200
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white
            focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
            rounded-[50px]
            text-base leading-[1.3] tracking-[0.32px]
            md:text-[calc(17/var(--base-fs)*1rem)] md:leading-[1.4] md:tracking-[0.34px]">
            {{ button.label }}
        </MessLink>
    </section>
</template>

<style>
.block-2026-faq {
    background: linear-gradient(214.5deg, var(--orange) 10.25%, var(--orange) 52.91%, #820ca7 103.29%);
}

/* CSS grid trick for smooth height animation */
.faq-answer {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 400ms ease;
}

.faq-answer.is-open {
    grid-template-rows: 1fr;
}

.faq-answer a {
    color: white;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
}

.faq-answer a:hover {
    text-decoration: none;
}

.faq-reveal {
    opacity: 0;
    transform: translateX(-30px);
    transition: opacity 500ms ease, transform 500ms ease;
}

.faq-reveal.is-visible {
    opacity: 1;
    transform: translateX(0);
}

@media (prefers-reduced-motion: reduce) {
    .faq-answer,
    .faq-reveal {
        transition: none;
    }

    .faq-reveal {
        opacity: 1;
        transform: none;
    }
}
</style>
